<script setup lang="ts">
import { ref, computed } from 'vue'
import { Check } from 'lucide-vue-next'
import PublicLayout from '@/components/PublicLayout.vue'

const billingCycle = ref<'monthly' | 'yearly'>('yearly')
const currency = ref<'IDR' | 'USD'>('IDR')

const pricing = {
  IDR: {
    free: 'Rp 0',
    pro: {
      annual: 'Rp 14,988,000',
      monthly: 'Rp 1,600,000',
      annualPerMonth: 'Rp 1,249,000'
    },
    templatePack: {
      annual: 'Rp 249,000/month',
      monthly: 'Rp 320,000/month'
    }
  },
  USD: {
    free: '$0',
    pro: {
      annual: '$1068',
      monthly: '$115',
      annualPerMonth: '$89'
    },
    templatePack: {
      annual: '$19/month',
      monthly: '$25/month'
    }
  }
}

const plans = computed(() => [
  {
    name: 'Free',
    subtitle: 'Get Started',
    price: pricing[currency.value].free,
    priceNote: 'Forever free',
    features: [
      'Basic document generation',
      '1 template only',
      'Slow server (shared resources)',
      'Manual generation only',
      'Community support',
      '1-day document retention'
    ],
    highlight: false
  },
  {
    name: 'Pro',
    subtitle: 'For Professional Use',
    annual: pricing[currency.value].pro.annual,
    monthly: pricing[currency.value].pro.monthly,
    annualPerMonth: pricing[currency.value].pro.annualPerMonth,
    discount: '22%',
    features: [
      'Unlimited document generation',
      'Up to 10 templates included',
      'PDF conversion',
      'Dynamic image replacement',
      'Image compression (reduce file size)',
      'Bulk generation',
      'Auto-save to Lark attachment fields',
      'Priority email & WhatsApp support',
      '7-day document retention',
      'Webhook notifications',
      'HTTP trigger for automation'
    ],
    addons: [
      {
        name: 'Additional Template Pack (10 templates)',
        annual: pricing[currency.value].templatePack.annual,
        monthly: pricing[currency.value].templatePack.monthly
      }
    ],
    highlight: true
  }
])
</script>

<template>
  <PublicLayout>
    <div class="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        <!-- Header -->
        <div class="text-center mb-8 sm:mb-12">
          <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3">Simple, Transparent Pricing</h1>
          <p class="text-base sm:text-lg text-gray-600 mb-6">Choose the plan that fits your document generation needs</p>

          <div class="flex flex-col items-center gap-4">
            <!-- Currency Toggle -->
            <div class="inline-flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
              <button
                @click="currency = 'IDR'"
                :class="[
                  'px-4 py-1.5 rounded-md font-medium transition-all text-sm',
                  currency === 'IDR'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                ]"
              >
                IDR (Rp)
              </button>
              <button
                @click="currency = 'USD'"
                :class="[
                  'px-4 py-1.5 rounded-md font-medium transition-all text-sm',
                  currency === 'USD'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                ]"
              >
                USD ($)
              </button>
            </div>

            <!-- Billing Cycle Toggle -->
            <div class="inline-flex items-center gap-3 bg-gray-100 p-1 rounded-lg">
              <button
                @click="billingCycle = 'monthly'"
                :class="[
                  'px-6 py-2 rounded-md font-medium transition-all',
                  billingCycle === 'monthly'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                ]"
              >
                Monthly
              </button>
              <button
                @click="billingCycle = 'yearly'"
                :class="[
                  'px-6 py-2 rounded-md font-medium transition-all',
                  billingCycle === 'yearly'
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                ]"
              >
                Yearly
                <span class="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Save 22%</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Pricing Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 max-w-5xl mx-auto">
          <div
            v-for="plan in plans"
            :key="plan.name"
            :class="[
              'rounded-lg p-8 border-2 transition-all',
              plan.highlight
                ? 'border-blue-500 shadow-xl relative'
                : 'border-gray-200 hover:border-gray-300 shadow-md'
            ]"
          >
            <!-- Popular Badge -->
            <div v-if="plan.highlight" class="absolute -top-4 left-1/2 -translate-x-1/2">
              <span class="bg-blue-500 text-white text-sm font-bold px-4 py-1 rounded-full">Recommended</span>
            </div>

            <!-- Plan Name -->
            <div class="text-center mb-6">
              <h3 class="text-2xl font-bold text-gray-800">{{ plan.name }}</h3>
              <p class="text-sm text-gray-600 mt-1">{{ plan.subtitle }}</p>
            </div>

            <!-- Price -->
            <div class="text-center mb-6">
              <div v-if="plan.name === 'Free'" class="text-4xl font-bold text-gray-800">{{ plan.price }}</div>
              <div v-else>
                <div class="text-4xl font-bold text-gray-800 mb-2">
                  {{ billingCycle === 'yearly' ? plan.annualPerMonth : plan.monthly }}
                  <span class="text-lg text-gray-600">/month</span>
                </div>
                <div class="text-sm text-gray-600">
                  {{ billingCycle === 'yearly' ? 'Billed annually' : 'Billed monthly' }}
                </div>
              </div>
              <div v-if="plan.name === 'Free'" class="text-sm text-gray-600">{{ plan.priceNote }}</div>
            </div>

            <!-- Features -->
            <div class="space-y-3 mb-8">
              <div v-for="(feature, index) in plan.features" :key="index" class="flex items-start gap-2">
                <Check :size="18" class="text-green-600 flex-shrink-0 mt-0.5" />
                <span class="text-sm text-gray-700">{{ feature }}</span>
              </div>
            </div>

            <!-- Add-ons -->
            <div v-if="plan.addons && plan.addons.length > 0" class="mb-8 pt-6 border-t border-gray-200">
              <h4 class="text-sm font-semibold text-gray-800 mb-3">Add-ons Available:</h4>
              <div v-for="(addon, index) in plan.addons" :key="index" class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                <div class="text-sm font-medium text-gray-800">{{ addon.name }}</div>
                <div class="text-sm text-gray-700 mt-1 font-semibold">
                  {{ billingCycle === 'yearly' ? addon.annual : addon.monthly }}
                </div>
              </div>
            </div>

            <!-- CTA Button -->
            <a
              href="mailto:christophe@ksi-digital.com?subject=AutomaDoc%20Pro%20-%20Inquiry"
              :class="[
                'block w-full text-center px-6 py-3 rounded-lg font-semibold transition-colors',
                plan.highlight
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              ]"
            >
              {{ plan.name === 'Free' ? 'Get Started' : 'Contact Sales' }}
            </a>
          </div>
        </div>

        <!-- Consultancy Section -->
        <div class="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-8 mb-8">
          <div class="text-center max-w-3xl mx-auto">
            <h3 class="text-2xl font-bold text-gray-800 mb-3">Consultancy Services</h3>
            <p class="text-gray-700 mb-6">
              Need help setting up your document templates or configuring your Lark Bases?
              Our expert team can assist you with custom template creation, workflow automation, and base setup.
            </p>
            <a
              href="mailto:christophe@ksi-digital.com?subject=AutomaDoc%20Consultancy%20-%20Inquiry"
              class="inline-block bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Us for Consultancy
            </a>
          </div>
        </div>

        <!-- Additional Info -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 class="text-lg font-semibold text-gray-800 mb-3">All Pro Plans Include</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div class="flex items-start gap-2">
              <Check :size="18" class="text-blue-600 flex-shrink-0 mt-0.5" />
              <span class="text-sm text-gray-700">Bulk document generation with ZIP download</span>
            </div>
            <div class="flex items-start gap-2">
              <Check :size="18" class="text-blue-600 flex-shrink-0 mt-0.5" />
              <span class="text-sm text-gray-700">Automatic saving to Lark attachment fields</span>
            </div>
            <div class="flex items-start gap-2">
              <Check :size="18" class="text-blue-600 flex-shrink-0 mt-0.5" />
              <span class="text-sm text-gray-700">Background async processing for large batches</span>
            </div>
            <div class="flex items-start gap-2">
              <Check :size="18" class="text-blue-600 flex-shrink-0 mt-0.5" />
              <span class="text-sm text-gray-700">Webhook notifications</span>
            </div>
            <div class="flex items-start gap-2">
              <Check :size="18" class="text-blue-600 flex-shrink-0 mt-0.5" />
              <span class="text-sm text-gray-700">12-month initial term with auto-renewal</span>
            </div>
            <div class="flex items-start gap-2">
              <Check :size="18" class="text-blue-600 flex-shrink-0 mt-0.5" />
              <span class="text-sm text-gray-700">Bank transfer payment (14-day terms)</span>
            </div>
          </div>
        </div>

        <!-- Contact Section -->
        <div class="text-center">
          <p class="text-gray-700 mb-4">Have questions about our pricing?</p>
          <a
            href="mailto:christophe@ksi-digital.com"
            class="inline-block bg-gray-800 hover:bg-gray-900 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            Contact Sales Team
          </a>
        </div>
    </div>
  </PublicLayout>
</template>
